<script lang="ts">
  import * as _ from "lodash";
  import type { RetailerUser } from "@shared/types/User";
  import { Http } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import SettingsPageWrapper from "@modern/components/retailer/settings/PageWrapper.svelte";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import { goto } from "@roxi/routify";

  export let usersRes;
  export let userId: string;

  const roles: { id: any; label: any }[] = window["C"].rolesNotInRetailerOrganizationMandate;
  let errors = { messages: [] };
  let passwordErrors = { messages: [] };

  let user: RetailerUser = _.find(usersRes, function (el) {
    return el.id === parseInt(userId);
  });

  if (!user) {
    Flash.queue({ message: "Sorry - that user is managing this location.", type: "error" });
    $goto("/settings/employees");
  } else {
    if (user.phone) {
      user.stringPhone = user.phone.toString();
    }
  }

  let currentUser: RetailerUser = $CurrentUserStore as RetailerUser;

  let submittingProfile = false;
  const updateProfile = function () {
    submittingProfile = true;
    Http(
      {
        method: "PUT",
        url: "/api/retailer/employees/" + userId,
        data: {
          user: _.merge(user, {
            phone: user.stringPhone,
          }),
        },
      },
      function (res) {
        submittingProfile = false;
        errors = { messages: [] };
        Flash.now({
          type: "success",
          message: "We have updated this employee's information",
        });
      },
      function (res) {
        submittingProfile = false;
        if (res.status >= 400 && res.status < 500) {
          errors = res.errors;
        } else {
          Flash.now({
            type: "error",
            message: "There was a server error when updating this information.",
          });
        }
      },
    );
  };

  const checkLacksPrivilege = function () {
    if (user && user.role === "group_director" && currentUser.role !== "group_director") {
      Flash.queue({
        message: "Sorry - you are not allowed to update group director information.",
        type: "error",
      });
      $goto("/settings/employees");
    }
  };
  checkLacksPrivilege();

  let newPass = "";
  let newPassConfirm = "";
  let submittingPassword = false;
  const updatePassword = function () {
    submittingPassword = true;
    Http(
      {
        method: "PUT",
        url: "/api/retailer/employees/" + userId + "/password",
        data: {
          user: {
            password: newPass,
            password_confirmation: newPassConfirm,
          },
        },
      },
      function (res) {
        passwordErrors = { messages: [] };
        submittingPassword = false;
        newPass = "";
        newPassConfirm = "";
        Flash.now({ message: "We have updated this employee's password", type: "success" });
      },
      function (res) {
        submittingPassword = false;
        if (res.status >= 400 && res.status < 500) {
          passwordErrors = res.errors;
        } else {
          Flash.now({
            message: "There was a server error when updating this information.",
            type: "error",
          });
        }
      },
    );
  };

  const updateEmployeeAssociation = function (association) {
    Http(
      {
        method: "PUT",
        url: "/api/retailer/employees/" + userId + "/" + association,
      },
      function (res) {
        $goto("/settings/employees");
        Flash.now({ message: res.msg, type: "success" });
      },
      function (res) {
        if (res.status >= 400 && res.status < 500) {
          errors = res.errors;
        } else {
          Flash.now({ message: "Oops! An error occured while updating this user.", type: "error" });
        }
      },
    );
  };

  const removeFromThisLocation = function () {
    let message = "Are you sure you want to remove this employee from this location?";
    if (confirm(message)) {
      updateEmployeeAssociation("remove_user_from_this_location");
    }
  };

  const removeFromAllLocationsIManage = function () {
    let message = "Are you sure you want to remove this employee from ALL locations you manage?";
    if (confirm(message)) {
      updateEmployeeAssociation("remove_user_from_all_locations_i_manage");
    }
  };
</script>

<SettingsPageWrapper>
  {#if user}
    <div class="padding--small">
      <div class="grid-row show--md">
        <div class="grid-col c12-12 c6-12--md vertical-align-middle margin-bottom--smaller">
          <UIRouterLink stateName="layout.users">
            <span class="brand-title--regular cursor-pointer margin-right--smaller"
              ><Arrow direction="left" /></span
            >
            <span class="default-title--regular">Employee Information</span>
          </UIRouterLink>
        </div>
      </div>
      <div class="margin-top--small">
        <FormErrors {errors} />
      </div>

      <div class="grid-row">
        <div class="grid-col c12-12">
          <div class="label input__label--left padding-bottom--smallest">Email Address</div>
          <div class="input-wrapper full-width">
            <input
              type="text"
              class="input"
              name="email"
              bind:value={user.email}
              disabled={submittingProfile}
            />
          </div>
          <div class="grid-col c12-12 c6-12--md">
            <div
              class="label input__label--left padding-bottom--smallest
              margin-top--small fizz-styles"
            >
              Cell Phone
              <span class="fizz-tooltip-container">
                <button class="fizz-tooltip-trigger" aria-describedby="cellphone-tooltip">
                  <Icon type="Question" height="16" width="16" color="#2196f3" />
                </button>
                <div class="fizz-tooltip" role="tooltip" id="cellphone-tooltip">
                  We'll only text critical order updates such as order rejections.
                </div>
              </span>
            </div>
            <div class="input-wrapper full-width">
              <PhoneInput
                bind:value={user.stringPhone}
                placeholder="Phone"
                disabled={submittingProfile}
                id="phone"
                extraClasses="phone-input input"
              />
            </div>
          </div>
        </div>
      </div>
      {#if currentUser.id !== user.id}
        <div class="grid-row">
          <div class="grid-col c12-12">
            <div class="label input__label--left margin-top--small padding-bottom--smallest">
              Role
            </div>
            <div class="input-wrapper select-style">
              <select id="role_select" bind:value={user.role}>
                {#each roles as role}
                  <option value={role.id}>{role.label}</option>
                {/each}
              </select>
            </div>
          </div>
        </div>
      {/if}

      <div class="grid-row padding-bottom--small--md">
        <div class="grid-col c12-12 c6-12--md">
          <div class="label input__label--left padding-bottom--smallest margin-top--small">
            First Name
          </div>
          <div class="input-wrapper full-width">
            <input
              type="text"
              class="input"
              name="first_name"
              bind:value={user.first_name}
              disabled={submittingProfile}
            />
          </div>
        </div>
        <div class="grid-col c12-12 c6-12--md">
          <div class="label input__label--left padding-bottom--smallest margin-top--small">
            Last Name
          </div>
          <div class="input-wrapper full-width">
            <input
              type="text"
              class="input"
              name="last_name"
              bind:value={user.last_name}
              disabled={submittingProfile}
            />
          </div>
        </div>
      </div>

      <div class="margin-top--small">
        <button
          class="button button--small button--primary submit-profile"
          disabled={submittingProfile}
          on:click={updateProfile}
        >
          Update
        </button>
      </div>

      <hr class="hr margin-top--small margin-bottom--small" />
      <div class="default-title--regular">Change Password</div>
      <div class="margin-top--small">
        <FormErrors errors={passwordErrors} />
      </div>

      <div class="grid-row">
        <div class="grid-col c12-12 c6-12--md">
          <div class="label input__label--left padding-bottom--smallest margin-top--small">
            New Password
          </div>
          <div class="input-wrapper full-width">
            <input
              type="password"
              class="input"
              name="password"
              bind:value={newPass}
              disabled={submittingPassword}
            />
          </div>
        </div>
      </div>

      <div class="grid-row">
        <div class="grid-col c12-12 c6-12--md margin-bottom--small">
          <div class="label input__label--left padding-bottom--smallest margin-top--small">
            Confirm New Password
          </div>
          <div class="input-wrapper full-width">
            <input
              type="password"
              class="input"
              name="password_confirmation"
              bind:value={newPassConfirm}
              disabled={submittingPassword}
            />
          </div>
        </div>
      </div>

      <div class="margin-top--small--md">
        <button
          class="button button--small button--primary submit-password"
          disabled={submittingPassword || !newPass.length || newPass != newPassConfirm}
          on:click={updatePassword}
        >
          Change Password
        </button>
      </div>

      <hr class="hr margin-top--small margin-bottom--small" />

      {#if currentUser.id !== user.id}
        <div>
          <div class="default-title--regular margin-bottom--small--md">Update Locations</div>
          <span
            class="default-body--bold color-red underline-hover cursor-pointer display-block margin-bottom--small"
            id="remove1"
            on:click={removeFromThisLocation}
            >Remove <span class="notranslate">{user.first_name}</span> from this location</span
          >
          <span
            class="default-body--bold color-red underline-hover cursor-pointer display-block margin-bottom--small"
            id="remove2"
            on:click={removeFromAllLocationsIManage}
            >Remove <span class="notranslate">{user.first_name}</span> from all locations you manage</span
          >
        </div>
      {/if}
    </div>
  {/if}
</SettingsPageWrapper>
